// ============================================================================
// Logo — Stokora brand mark.
//
// A gradient tile holding two candlesticks (down → up) with a subtle wick
// shadow. The up candle "breathes" on a 3.6s loop to add quiet life
// without distracting; the whole mark scales slightly on hover when the
// cluster is interactive. Wordmark uses a gradient-clipped fill so the
// brand colour reads even on light surfaces.
//
// Props:
//   size         — base size in px (default 18); tile is size+14, wordmark
//                  font-size tracks it.
//   onClick      — when present, the whole cluster becomes a button.
//   showWordmark — toggle "Stokora" text alongside the mark.
//   animated     — disable the breathe loop (e.g. for static contexts).
// ============================================================================

export default function Logo({ size = 18, onClick, showWordmark = true, animated = true }) {
  const tile = size + 14;
  const interactive = !!onClick;

  return (
    <span
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(e); } : undefined}
      className="stokora-logo"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: Math.round(size * 0.55),
        cursor: interactive ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'transform var(--dur-fast) var(--ease-out)',
      }}
    >
      <span
        className="stokora-logo-tile"
        style={{
          position: 'relative',
          width: tile, height: tile,
          borderRadius: 'var(--r-md)',
          background: 'var(--grad-accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 6px 18px var(--accent-glow), inset 0 1px 0 rgba(255,255,255,0.18)',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {/* Soft top-light sheen so the tile reads as 3D, not flat */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(120% 60% at 50% 0%, rgba(255,255,255,0.22), transparent 60%)',
            pointerEvents: 'none',
          }}
        />
        <svg
          width={size + 2} height={size + 2} viewBox="0 0 24 24" fill="none"
          stroke="#fff" strokeLinecap="round" aria-hidden="true"
          style={{ position: 'relative', zIndex: 1 }}
        >
          {/* Down candle — muted red side */}
          <line x1="8"   y1="4.5" x2="8"   y2="19.5" strokeWidth="1.4" opacity="0.75" />
          <rect x="5.6" y="9.2" width="4.8" height="6.2" rx="1.1"
            fill="#fff" stroke="none" opacity="0.5" />
          {/* Up candle — confident, breathes on a slow loop */}
          <line x1="16" y1="3" x2="16" y2="21" strokeWidth="1.4" opacity="0.9" />
          <rect
            x="13.6" y="6.6" width="4.8" height="8" rx="1.1"
            fill="#fff" stroke="none"
            className={animated ? 'stokora-logo-up' : ''}
            style={{ transformOrigin: '16px 14.6px' }}
          />
        </svg>
      </span>

      {showWordmark && (
        <span
          className="stokora-logo-word"
          style={{
            fontSize: size,
            fontWeight: 800,
            letterSpacing: '-0.04em',
            background: 'linear-gradient(135deg, var(--text-primary) 0%, var(--accent-soft) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Stokora
        </span>
      )}

      <style>{`
        .stokora-logo:hover .stokora-logo-tile {
          transform: translateY(-1px) scale(1.04);
          transition: transform var(--dur-fast) var(--ease-out);
        }
        .stokora-logo .stokora-logo-tile { transition: transform var(--dur-fast) var(--ease-out); }
        @keyframes stokoraBreathe {
          0%, 100% { transform: scaleY(1);    opacity: 1; }
          50%      { transform: scaleY(1.08); opacity: 0.92; }
        }
        .stokora-logo-up {
          animation: stokoraBreathe 3.6s var(--ease-in-out) infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .stokora-logo-up { animation: none; }
        }
      `}</style>
    </span>
  );
}
